import { useState, useEffect, useRef, useCallback } from "react";
import { API, useAuth, authFetch } from "@/App";
import { useSocketIo } from "@/hooks/useSocketIo";
import { toast } from "sonner";
import {
  MessageSquare, Plus, Send, Search, Paperclip, Mic, MicOff, Timer,
  File, Image, X, Download, Play, Pause, Clock, Camera, FileText, Images,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ChatPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [employees, setEmployees] = useState([]);
  const [newConvoOpen, setNewConvoOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const messagesEnd = useRef(null);
  // Attachment state
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Voice note state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  // Disappearing message state
  const [disappearing, setDisappearing] = useState(false);
  const [disappearSecs, setDisappearSecs] = useState(30);
  // Playback
  const [playingAudio, setPlayingAudio] = useState(null);

  useEffect(() => { fetchConversations(); fetchEmployees(); }, []);
  useEffect(() => { if (selectedConvo) fetchMessages(selectedConvo.conversation_id); }, [selectedConvo]);
  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Real-time subscription for new messages — Socket.IO (migrated from /api/ws/notifications, P1 iter 73).
  // Backend emits chat_message events via core/realtime.emit_chat_message after
  // every POST /api/v1/chat/messages, and also fans the legacy 'new_message'
  // broadcast through broadcast_realtime_update -> Socket.IO.
  useSocketIo({
    chat_message: (msg) => {
      if (!msg) return;
      setSelectedConvo((sc) => {
        if (sc && sc.conversation_id === msg.conversation_id) {
          setMessages((prev) => prev.some((m) => m.message_id === msg.message_id) ? prev : [...prev, msg]);
        }
        return sc;
      });
      fetchConversations();
    },
    realtime_update: (payload) => {
      if (payload?.event === "new_message" && payload?.data) {
        const msg = payload.data;
        setSelectedConvo((sc) => {
          if (sc && sc.conversation_id === msg.conversation_id) {
            setMessages((prev) => prev.some((m) => m.message_id === msg.message_id) ? prev : [...prev, msg]);
          }
          return sc;
        });
        fetchConversations();
      }
    },
  }, { enabled: !!user?.user_id });

  const fetchConversations = async () => {
    try {
      const res = await authFetch(`${API}/chat/conversations`);
      const data = await res.json();
      if (data.success) setConversations(data.data || []);
    } catch {} finally { setLoading(false); }
  };

  const fetchMessages = async (convoId) => {
    try {
      const res = await authFetch(`${API}/chat/messages/${convoId}`);
      const data = await res.json();
      if (data.success) {
        setMessages(data.data || []);
        await authFetch(`${API}/chat/messages/${convoId}/read`, { method: "POST" });
      }
    } catch {}
  };

  const fetchEmployees = async () => {
    try {
      const res = await authFetch(`${API}/chat/users`);
      const data = await res.json();
      if (data.success) {
        const filtered = (data.data || []).filter(u => u.user_id && u.user_id !== user?.user_id);
        setEmployees(filtered);
      }
    } catch {}
  };

  const sendTextMessage = async () => {
    if (!newMessage.trim() && !attachmentFile) return;
    if (!selectedConvo) return;

    let attachment = null;
    let msgType = "text";

    // Upload attachment if present
    if (attachmentFile) {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", attachmentFile);
        const res = await authFetch(`${API}/chat/upload`, { method: "POST", body: formData });
        const data = await res.json();
        if (data.success) {
          attachment = data.data;
          msgType = attachmentFile.type.startsWith("image/") ? "image" : "file";
        } else { toast.error("Failed to upload file"); setUploading(false); return; }
      } catch { toast.error("Upload failed"); setUploading(false); return; }
      setUploading(false);
    }

    try {
      const res = await authFetch(`${API}/chat/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversation_id: selectedConvo.conversation_id,
          content: newMessage.trim(),
          message_type: msgType,
          attachment,
          disappearing,
          disappear_after_secs: disappearing ? disappearSecs : null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setMessages((m) => [...m, data.data]);
        setNewMessage("");
        setAttachmentFile(null);
        fetchConversations();
      }
    } catch { toast.error("Failed to send message"); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        const file = new File([blob], `voice_${Date.now()}.webm`, { type: "audio/webm" });
        // Upload voice note
        setUploading(true);
        try {
          const formData = new FormData();
          formData.append("file", file);
          const res = await authFetch(`${API}/chat/upload`, { method: "POST", body: formData });
          const data = await res.json();
          if (data.success) {
            // Send as voice message
            const msgRes = await authFetch(`${API}/chat/messages`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                conversation_id: selectedConvo.conversation_id,
                content: "Voice note",
                message_type: "voice",
                attachment: data.data,
                disappearing,
                disappear_after_secs: disappearing ? disappearSecs : null,
              }),
            });
            const msgData = await msgRes.json();
            if (msgData.success) {
              setMessages((m) => [...m, msgData.data]);
              fetchConversations();
            }
          }
        } catch { toast.error("Failed to send voice note"); }
        setUploading(false);
      };
      recorder.start();
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setIsRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const createConversation = async () => {
    if (!selectedEmployee) return;
    try {
      const res = await authFetch(`${API}/chat/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipient_id: selectedEmployee }),
      });
      const data = await res.json();
      if (data.success) {
        setNewConvoOpen(false);
        setSelectedEmployee("");
        fetchConversations();
        setSelectedConvo(data.data);
      } else {
        toast.error(data.detail || "Failed to start chat");
      }
    } catch { toast.error("Failed to create conversation"); }
  };

  const isFromMe = (msg) => msg.sender_id === user?.user_id;

  // Avatar circle for chat list + incoming messages.
  // Prefers participant_photos[user_id] from backend (if set), falls back to
  // first-letter on a deterministic colour derived from the user_id hash.
  const Avatar = ({ name, userId, photoPath, size = 36, className = "" }) => {
    const initial = (name || "?").trim().charAt(0).toUpperCase();
    // Deterministic colour from user_id
    let hash = 0;
    for (const ch of (userId || name || "x")) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
    const palette = ["#8B1A1A", "#1A1A2E", "#0F766E", "#7C3AED", "#0369A1", "#B45309", "#BE185D", "#4338CA"];
    const bg = palette[Math.abs(hash) % palette.length];
    const dim = { width: size, height: size, fontSize: Math.floor(size / 2.6) };
    if (photoPath) {
      // photoPath is expected to be a /api/files/<storage_path>
      // (employee profile photo). Use the session-token auth pattern.
      const tok = typeof localStorage !== "undefined" ? localStorage.getItem("session_token") : "";
      return (
        <img
          src={`${API}/files/${photoPath}?auth=${tok}`}
          alt={name || "user"}
          className={`rounded-full object-cover ${className}`}
          style={dim}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          data-testid={`avatar-photo-${userId || "x"}`}
        />
      );
    }
    return (
      <div
        className={`rounded-full flex items-center justify-center text-white font-semibold shrink-0 ${className}`}
        style={{ ...dim, backgroundColor: bg }}
        data-testid={`avatar-initial-${userId || "x"}`}
      >
        {initial}
      </div>
    );
  };

  // Safely render last_message preview — backend may return either a string
  // (legacy) or a chat-message object (v1). Avoid passing the object straight
  // to React children which throws "Objects are not valid as a React child".
  const formatLastMessage = (lm) => {
    if (!lm) return "No messages yet";
    if (typeof lm === "string") return lm;
    if (typeof lm === "object") {
      if (lm.content) return lm.content;
      const t = (lm.message_type || "attachment").toLowerCase();
      if (t === "voice") return "Voice note";
      if (t === "image") return "Photo";
      if (t === "file") return lm.attachment?.original_filename || "File";
      if (t === "rich_card") return lm.rich_card?.title || "Rich card";
      return "Attachment";
    }
    return String(lm);
  };
  const shouldHide = (msg) => {
    if (!msg.disappearing) return false;
    const sent = new Date(msg.timestamp).getTime();
    const now = Date.now();
    return now - sent > (msg.disappear_after_secs || 604800) * 1000;
  };

  const visibleMessages = messages.filter(m => !shouldHide(m));

  const renderAttachment = (msg) => {
    if (!msg.attachment) return null;
    const att = msg.attachment;
    if (msg.message_type === "voice") {
      return (
        <div className="flex items-center gap-2 mt-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid="play-voice"
            onClick={() => {
              const url = `${API}/files/${att.storage_path}?auth=${localStorage.getItem("session_token")}`;
              if (playingAudio) { playingAudio.pause(); setPlayingAudio(null); return; }
              const audio = new Audio(url);
              audio.play();
              audio.onended = () => setPlayingAudio(null);
              setPlayingAudio(audio);
            }}>
            {playingAudio ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </Button>
          <div className="h-1 flex-1 bg-slate-200 rounded-full"><div className="h-1 bg-[#8B1A1A] rounded-full w-1/2"></div></div>
          <span className="text-[10px] text-slate-400">Voice</span>
        </div>
      );
    }
    if (msg.message_type === "image") {
      return <div className="mt-1 rounded-md overflow-hidden max-w-[200px]">
        <img src={`${API}/files/${att.storage_path}?auth=${localStorage.getItem("session_token")}`} alt="" className="w-full rounded" data-testid="chat-image" />
      </div>;
    }
    // File attachment
    return (
      <div className="mt-1 flex items-center gap-2 p-2 bg-white/50 rounded border text-xs">
        <File className="w-3.5 h-3.5 text-slate-500" />
        <span className="truncate flex-1">{att.original_filename}</span>
        <a href={`${API}/files/${att.storage_path}?auth=${localStorage.getItem("session_token")}`} target="_blank" rel="noreferrer"
          className="text-[#8B1A1A] hover:underline" data-testid="download-file"><Download className="w-3.5 h-3.5" /></a>
      </div>
    );
  };

  return (
    <div className="flex h-[calc(100vh-140px)] gap-4" data-testid="chat-page">
      {/* Conversations List */}
      <Card className="w-80 shrink-0 flex flex-col overflow-hidden">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold text-slate-900">Messages</h2>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setNewConvoOpen(true)} data-testid="new-convo-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input placeholder="Search..." className="h-8 pl-8 text-sm" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="chat-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <div className="flex items-center justify-center py-8"><div className="w-5 h-5 spinner"></div></div> :
            conversations.filter(c => !search || c.title?.toLowerCase().includes(search.toLowerCase())).map((convo) => (
              <div key={convo.conversation_id} onClick={() => setSelectedConvo(convo)}
                className={`p-3 cursor-pointer border-b border-slate-50 transition hover:bg-slate-50
                  ${selectedConvo?.conversation_id === convo.conversation_id ? "bg-[#8B1A1A]/5 border-l-2 border-l-[#8B1A1A]" : ""}`}
                data-testid={`convo-${convo.conversation_id}`}>
                <div className="flex items-center gap-2">
                  {(() => {
                    const otherId = (convo.participants || []).find((p) => p !== user?.user_id);
                    const otherName = convo.participant_names?.[otherId] || convo.title || "Chat";
                    const otherPhoto = convo.participant_photos?.[otherId];
                    return <Avatar name={otherName} userId={otherId} photoPath={otherPhoto} size={36} />;
                  })()}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{convo.title || "Chat"}</p>
                    <p className="text-xs text-slate-500 truncate">{formatLastMessage(convo.last_message)}</p>
                  </div>
                  {convo.unread_count > 0 && <Badge className="bg-[#8B1A1A] text-white text-[10px] h-5 min-w-[20px] flex items-center justify-center">{convo.unread_count}</Badge>}
                </div>
              </div>
            ))}
        </div>
      </Card>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        {selectedConvo ? (
          <>
            {/* Header */}
            <div className="p-3 border-b flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900 text-sm">
                  {selectedConvo.participant_names
                    ? Object.entries(selectedConvo.participant_names).filter(([uid]) => uid !== user?.user_id).map(([, n]) => n).join(", ") || "Chat"
                    : selectedConvo.title || "Chat"}
                </h3>
                <p className="text-xs text-slate-500">{selectedConvo.participants?.length || 0} participants</p>
              </div>
              {/* Disappearing toggle */}
              <div className="flex items-center gap-2" data-testid="disappearing-toggle">
                <Timer className={`w-4 h-4 ${disappearing ? "text-[#8B1A1A]" : "text-slate-400"}`} />
                <span className="text-xs text-slate-600">Disappearing</span>
                <Switch checked={disappearing} onCheckedChange={setDisappearing} data-testid="switch-disappearing" />
                {disappearing && (
                  <Select value={String(disappearSecs)} onValueChange={(v) => setDisappearSecs(parseInt(v))}>
                    <SelectTrigger className="w-24 h-7 text-xs" data-testid="disappear-time"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3600">1 hour</SelectItem>
                      <SelectItem value="86400">1 day</SelectItem>
                      <SelectItem value="604800">1 week</SelectItem>
                      <SelectItem value="2592000">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-container">
              {visibleMessages.map((msg) => (
                <div key={msg.message_id} className={`flex ${isFromMe(msg) ? "justify-end" : "justify-start"} gap-2`}>
                  {!isFromMe(msg) && (
                    <Avatar
                      name={msg.sender_name}
                      userId={msg.sender_id}
                      photoPath={msg.sender_photo}
                      size={28}
                      className="mt-0.5 self-end"
                    />
                  )}
                  <div className={`max-w-[70%] rounded-lg px-3 py-2 ${isFromMe(msg) ? "bg-[#8B1A1A] text-white" : "bg-slate-100 text-slate-800"}`}
                    data-testid={`msg-${msg.message_id}`}>
                    {!isFromMe(msg) && <p className="text-[10px] font-semibold opacity-70 mb-0.5">{msg.sender_name}</p>}
                    {msg.content && <p className="text-sm">{msg.content}</p>}
                    {renderAttachment(msg)}
                    <div className="flex items-center gap-1 mt-1 justify-end">
                      {msg.disappearing && <Clock className={`w-2.5 h-2.5 ${isFromMe(msg) ? "text-white/50" : "text-slate-400"}`} />}
                      <span className={`text-[10px] ${isFromMe(msg) ? "text-white/60" : "text-slate-400"}`}>
                        {new Date(msg.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEnd} />
            </div>

            {/* Input Area */}
            <div className="p-3 border-t">
              {attachmentFile && (
                <div className="mb-2 flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-md text-sm">
                  <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                  <span className="truncate flex-1 text-slate-600">{attachmentFile.name}</span>
                  <button onClick={() => setAttachmentFile(null)} className="text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                </div>
              )}
              <div className="flex items-center gap-2">
                {/* Camera capture (mobile-friendly) */}
                <label className="cursor-pointer p-1 hover:bg-slate-100 rounded-md" data-testid="attach-camera-btn" title="Take photo">
                  <Camera className="w-5 h-5 text-slate-400 hover:text-[#8B1A1A] transition" />
                  <input type="file" className="hidden" accept="image/*" capture="environment"
                    onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)} />
                </label>
                {/* Photo gallery */}
                <label className="cursor-pointer p-1 hover:bg-slate-100 rounded-md" data-testid="attach-gallery-btn" title="Pick from gallery">
                  <Images className="w-5 h-5 text-slate-400 hover:text-[#8B1A1A] transition" />
                  <input type="file" className="hidden" accept="image/*"
                    onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)} />
                </label>
                {/* Documents */}
                <label className="cursor-pointer p-1 hover:bg-slate-100 rounded-md" data-testid="attach-doc-btn" title="Attach document">
                  <FileText className="w-5 h-5 text-slate-400 hover:text-[#8B1A1A] transition" />
                  <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                    onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)} />
                </label>
                {/* Voice note */}
                <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${isRecording ? "text-red-500 animate-pulse" : "text-slate-400 hover:text-[#8B1A1A]"}`}
                  onClick={isRecording ? stopRecording : startRecording} data-testid="voice-note-btn"
                  disabled={!selectedConvo || uploading}>
                  {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </Button>
                <Input className="flex-1 h-9" placeholder={uploading ? "Uploading..." : isRecording ? "Recording..." : "Type a message..."}
                  value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTextMessage(); } }}
                  disabled={uploading || isRecording} data-testid="message-input" />
                <Button className="btn-primary h-9 w-9 p-0" onClick={sendTextMessage}
                  disabled={uploading || isRecording || (!newMessage.trim() && !attachmentFile)} data-testid="send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
            <MessageSquare className="w-12 h-12 mb-3 text-slate-300" />
            <p className="font-medium">Select a conversation</p>
            <p className="text-sm">or start a new one</p>
          </div>
        )}
      </Card>

      {/* New Conversation Dialog */}
      <Dialog open={newConvoOpen} onOpenChange={setNewConvoOpen}>
        <DialogContent className="max-w-sm" data-testid="new-convo-dialog">
          <DialogHeader><DialogTitle>New Conversation</DialogTitle></DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-slate-700 block mb-2">Select Employee</label>
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger data-testid="select-employee"><SelectValue placeholder="Choose employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.user_id} value={emp.user_id}>{emp.full_name || emp.name || emp.email} {emp.role ? `(${emp.role})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewConvoOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={createConversation} disabled={!selectedEmployee} data-testid="create-convo-btn">Start Chat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatPage;
